import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { X, UserCheck, Trash2 } from "lucide-react";
import {
  getClass,
  setClassTeacher,
  assignTeacherToSubject,
  removeTeacherFromSubject,
} from "../../services/classes";
import { getTeachers } from "../../services/teachers";

interface TeacherAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: string;
}

const TeacherAssignmentModal: React.FC<TeacherAssignmentModalProps> = ({
  isOpen,
  onClose,
  classId,
}) => {
  const queryClient = useQueryClient();

  // Fetch class data
  const {
    data: classResponse,
    isLoading: classLoading,
    error: classError,
  } = useQuery({
    queryKey: ["class", classId],
    queryFn: () => getClass(classId),
    enabled: isOpen && !!classId && classId.length === 24, // Ensure valid ObjectId length
    retry: 1, // Only retry once
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch available teachers
  const { data: teachersResponse } = useQuery({
    queryKey: ["teachers"],
    queryFn: () => getTeachers({ limit: 100 }),
    enabled: isOpen,
  });

  const classData = classResponse?.class;
  const availableTeachers = teachersResponse?.data || [];

  // Set class teacher mutation
  const setClassTeacherMutation = useMutation({
    mutationFn: ({ teacherId }: { teacherId?: string }) =>
      setClassTeacher(classId, teacherId),
    onSuccess: () => {
      toast.success("Class teacher updated successfully");
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to update class teacher"
      );
    },
  });

  // Remove teacher from subject mutation
  const removeTeacherMutation = useMutation({
    mutationFn: ({
      subjectName,
      teacherId,
    }: {
      subjectName: string;
      teacherId: string;
    }) => removeTeacherFromSubject(classId, subjectName, teacherId),
    onSuccess: () => {
      toast.success("Teacher removed from subject successfully");
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to remove teacher from subject"
      );
    },
  });

  if (!isOpen) return null;

  const handleSetClassTeacher = (teacherId: string) => {
    setClassTeacherMutation.mutate({ teacherId });
  };

  const handleRemoveClassTeacher = () => {
    if (window.confirm("Are you sure you want to remove the class teacher?")) {
      setClassTeacherMutation.mutate({ teacherId: undefined });
    }
  };

  const handleAssignTeacherToSubject = (
    subjectName: string,
    teacherId: string
  ) => {
    assignTeacherMutation.mutate({ subjectName, teacherId });
  };

  const handleRemoveTeacherFromSubject = (
    subjectName: string,
    teacherId: string
  ) => {
    removeTeacherMutation.mutate({ subjectName, teacherId });
  };

  const currentClassTeacher = availableTeachers.find(
    (teacher) => teacher._id === classData?.classTeacher
  );

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity z-[9998]"
          onClick={onClose}
        ></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full relative z-[10000]">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                  <UserCheck className="h-6 w-6 text-green-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Manage Teacher Assignment
                  </h3>
                  <p className="text-sm text-gray-500">
                    {classData?.name} - {classData?.grade}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-4 pb-4 sm:p-6">
            {classLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : classError ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="text-red-600 mb-2">
                    <X className="h-12 w-12 mx-auto" />
                  </div>
                  <p className="text-gray-600">
                    Failed to load class details. Please try again.
                  </p>
                  <button
                    onClick={onClose}
                    className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : !classData ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="text-gray-600">No class data available</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Class Teacher Section */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Class Teacher
                  </h4>
                  <div className="flex items-center justify-between">
                    <div>
                      {currentClassTeacher ? (
                        <div>
                          <p className="font-medium text-gray-900">
                            {currentClassTeacher.userInfo?.firstName}{" "}
                            {currentClassTeacher.userInfo?.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {currentClassTeacher.userInfo?.email}
                          </p>
                        </div>
                      ) : (
                        <p className="text-gray-500">
                          No class teacher assigned
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <select
                        onChange={(e) => {
                          if (
                            e.target.value &&
                            e.target.value !== classData?.classTeacher
                          ) {
                            handleSetClassTeacher(e.target.value);
                            e.target.value = "";
                          }
                        }}
                        className="rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                        disabled={setClassTeacherMutation.isPending}
                      >
                        <option value="">
                          {currentClassTeacher
                            ? "Change teacher..."
                            : "Assign teacher..."}
                        </option>
                        {availableTeachers.map((teacher) => (
                          <option key={teacher._id} value={teacher._id}>
                            {teacher.userInfo?.firstName}{" "}
                            {teacher.userInfo?.lastName}
                          </option>
                        ))}
                      </select>
                      {currentClassTeacher && (
                        <button
                          onClick={handleRemoveClassTeacher}
                          disabled={setClassTeacherMutation.isPending}
                          className="text-red-600 hover:text-red-800 p-1 rounded"
                          title="Remove Class Teacher"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Subject Teachers Section */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Subject Teachers
                  </h4>
                  <div className="space-y-3">
                    {classData?.subjects && classData.subjects.length > 0 ? (
                      classData.subjects.map((subject: any, index: number) => (
                        <div
                          key={index}
                          className="bg-white rounded border p-3"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="font-medium text-gray-900">
                                {subject.name || subject.subjectName}
                              </h5>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {subject.assignedTeacherIds &&
                                subject.assignedTeacherIds.length > 0 ? (
                                  subject.assignedTeacherIds.map(
                                    (
                                      teacherId: string,
                                      teacherIndex: number
                                    ) => {
                                      const teacher = availableTeachers.find(
                                        (t) => t._id === teacherId
                                      );
                                      return (
                                        <div
                                          key={teacherIndex}
                                          className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800"
                                        >
                                          <span>
                                            {teacher
                                              ? `${
                                                  teacher.userInfo?.firstName ||
                                                  "Unknown"
                                                } ${
                                                  teacher.userInfo?.lastName ||
                                                  "Teacher"
                                                }`
                                              : `Teacher ID: ${teacherId}`}
                                          </span>
                                          <button
                                            onClick={() => {
                                              if (
                                                window.confirm(
                                                  "Remove this teacher from the subject?"
                                                )
                                              ) {
                                                // We'll need to implement remove functionality
                                                handleRemoveTeacherFromSubject(
                                                  subject.name ||
                                                    subject.subjectName,
                                                  teacherId
                                                );
                                              }
                                            }}
                                            className="ml-1 text-green-600 hover:text-green-800"
                                            title="Remove teacher"
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        </div>
                                      );
                                    }
                                  )
                                ) : (
                                  <span className="text-sm text-gray-500">
                                    No teacher assigned
                                  </span>
                                )}
                              </div>
                            </div>
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleAssignTeacherToSubject(
                                    subject.name || subject.subjectName,
                                    e.target.value
                                  );
                                  e.target.value = "";
                                }
                              }}
                              className="rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                              disabled={
                                assignTeacherMutation.isPending ||
                                removeTeacherMutation.isPending
                              }
                            >
                              <option value="">Assign teacher...</option>
                              {availableTeachers.map((teacher) => (
                                <option key={teacher._id} value={teacher._id}>
                                  {teacher.userInfo?.firstName}{" "}
                                  {teacher.userInfo?.lastName}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">
                        No subjects found for this class
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherAssignmentModal;
